import React, { useState } from "react"
import clsx from "clsx"
import { isMobileOnly } from "react-device-detect"
import { useTranslation } from "react-i18next"
import { NavLink, useNavigate, useLocation, useParams } from "react-router-dom"
import * as PartnerApi from "features/Partner/api/PartnerApi"
import * as UserApi from "features/api/UserApi"
import ProtectedLayout from "layouts/Protected"
import { privateRoutes, publicRoutes } from "router/routes"
import { useAuth, useAlert } from "hooks"
import { isDefined } from "utils/validation"
import { jsonHash } from "utils/hash"
import { sortByProperty, openInNewTab } from "utils/functions"
import CaretIcon from "components/Elements/Icons/Caret"
import { isNotEmpty } from "utils/validation"
import { VELOCISTE_GENDER } from "utils/constants"
import { http } from "services/http"
import { prepareDataToPut } from "./hooks/prepareDataToPut"
import { fetchBOPartner } from "./hooks/fetchBOPartner"
// Partners components
import {
	ReferenceForm,
	OperatorsForm,
	StructureForm,
	ServicesForm,
	AddressForm,
	ManagerForm,
	BilingForm,
	MandateForm,
	BikeCollectionImportForm,
} from "./Partials"
import {} from "utils/constants"
import BikeFleetAccountIdForm from "./Partials/BikeFleetAccountIdForm"

const defaultFormEvents = {
	ready: true,
	editable: false,
}

// const PERMISSION_STORE = 'MANAGEBIKES'

export const PartnerPage = () => {
	const [partner, setPartner] = React.useState(null)
	const [updatedPartner, setUpdatedPartner] = React.useState(partner)
	const [formEvents, setFormEvents] = React.useState(defaultFormEvents)

	const [errors, setErrors] = React.useState([])
	const [showLocalErrors, setShowLocalErrors] = React.useState(false)
	const [jsonHashEntityData, setJsonHashEntityData] = React.useState({})
	const [jsonHashOperatorData, setJsonHashOperatorData] = React.useState({})

	const { alert } = useAlert()
	const { t } = useTranslation()
	const {
		logout,
		isAdminOCode,
		isAdminBicycode,
		isAdminMFC,
		isAdminMoustache,
		isAdminOperator,
		isAdmin,
	} = useAuth()
	const { pathname } = useLocation()
	const { slug } = useParams()
	const navigate = useNavigate()

	const isPartnerAlreadyRegistered = isDefined(slug)
	const isProductionEnvironment = process.env.REACT_APP_MODE === "production" // Ne pas afficher le bloc Mandat en production sans le raccordement avec sage
	const [editForm, setEditForm] = useState(false)

	const fetchData = async () => {
		try {
			const fetchedPartner = await fetchBOPartner(
				slug,
				isAdminOCode,
				setJsonHashEntityData,
				setJsonHashOperatorData
			)
			setPartner(fetchedPartner)
		} catch (error) {
			console.error(error)
			const erroMsg = error?.message?.toString() || t("global_fetch_error", { ns: "partner" })
			if (erroMsg) alert("error", erroMsg)

			if (
				["InvalidArgument", "EntityStatusException", "InternalServer"].includes(error?.code)
			)
				navigate(-1)
			if (["NotAuthorizedException"].includes(error?.code)) logout()
		}
	}

	React.useEffect(() => {
		if (!isNotEmpty(partner)) fetchData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const createPartner = async () => {
		try {
			// hide error
			setShowLocalErrors(false)
			// prevent form to be submitted again
			setFormEvents({
				...formEvents,
				ready: false,
			})

			const newPartner = prepareDataToPut({
				partner: { ...partner, ...updatedPartner },
				isAdminMFC,
				isAdminBicycode,
				isAdminMoustache,
			})

			const { getError } = await PartnerApi.createPartner(newPartner)
			if (getError()) throw getError()

			alert("success", t("edit_success", { ns: "partner" }))
			navigate(privateRoutes.validatedPartner.path)
		} catch (error) {
			console.error(error)
			const erroMsg = error?.message?.toString()
			if (erroMsg) alert("error", erroMsg)

			if (["Access token is required."].includes(error?.error?.message)) logout()
		} finally {
			setFormEvents((s) => ({
				...s,
				ready: true,
			}))
		}
	}

	const editPartner = async () => {
		try {
			// hide error
			setShowLocalErrors(false)
			// prevent form to be submitted again
			setFormEvents((s) => ({
				...s,
				ready: false,
			}))

			const newPartner = prepareDataToPut({
				isUpdate: true,
				partner: { ...partner, ...updatedPartner },
				isAdminMFC,
				isAdminBicycode,
				isAdminMoustache,
			})

			let updateEntityData = true
			if (jsonHash(newPartner.entity_data) === jsonHashEntityData) {
				updateEntityData = false
			}
			const data_operators = JSON.parse(JSON.stringify(newPartner.data_operators))

			let updateOperator = true
			if (
				jsonHash(data_operators.sort(sortByProperty("operator"))) === jsonHashOperatorData
			) {
				updateOperator = false
			}

			if (!updateEntityData && !updateOperator) {
				alert("error", t("edit_error_TooManyRequestsException", { ns: "api" }))
				return
			} else {
				if (updateEntityData) {
					delete newPartner.data_operators
					const { getError } = await PartnerApi.updatePartnerAccount(newPartner)
					if (getError()) throw getError()
				}
				if (updateOperator) {
					const { res: updatedPartner } = await PartnerApi.getPartner(
						newPartner.entity_id
					)
					const newOperator = {
						entity_id: newPartner.entity_id,
						data_operators: data_operators,
						last_row_hash: updatedPartner.row_hash,
					}

					const { getError } = await PartnerApi.updatePartnerOperator(newOperator)
					if (getError()) throw getError()
				}
				alert("success", t("edit_success", { ns: "partner" }))
			}
			setEditForm(false)
		} catch (error) {
			console.error(error)
			const erroMsg = error?.message?.toString()
			if (erroMsg) alert("error", erroMsg)
		} finally {
			await fetchData()
			setFormEvents((s) => ({
				...s,
				ready: true,
				editable: false,
			}))
		}
	}

	const handleEdit = () => {
		setEditForm(true)
	}

	const handleUpdatePartner = async () => {
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
			alert("error", t("global.form_error"))
			// TODO: inner scroll inside component when error
			window.scrollTo({
				top: 0,
				behavior: "smooth",
			})
		} else {
			await editPartner()
		}
	}

	const handleCreatePartner = async () => {
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
			alert("error", t("global.form_error"))
			// TODO: inner scroll inside component when error
			window.scrollTo({
				top: 0,
				behavior: "smooth",
			})
		} else {
			await createPartner()
		}
	}

	// ADMIN SPECIFIC FEAT.
	// ----------------------
	const connectAsPartner = async () => {
		const partnerId = partner.entity_id
		try {
			// Get backend access token (token to access platform as backend)
			// (token should not be automatically stored by Axios)
			const { res: connectedPartner, getError } = await UserApi.signInAsSupport(partnerId)
			if (getError()) throw getError()

			const backendAccessToken = connectedPartner?.backend_access_token
			if (!backendAccessToken) throw new Error("backend access token is missing")

			const isBackendAccessTokenExpired = http.isTokenExpired(backendAccessToken)
			if (isBackendAccessTokenExpired) throw new Error("backend access token is expired")

			const fullUrl = window.location.href
			const baseUrl = fullUrl.replace(pathname, "")

			openInNewTab(
				`${baseUrl}${publicRoutes.login.path}?backend_access_token=${backendAccessToken}`,
				{ focus: true }
			)
		} catch (error) {
			// console.log("error: ", error)
			alert("error", t("support.fetch_error"))
		}
	}

	const componentCommonProps = {
		partner: partner,
		updatedPartner: updatedPartner,
		setUpdatedPartner: setUpdatedPartner,
		errors: errors,
		setErrors: setErrors,
		showLocalErrors: showLocalErrors,
		setShowLocalErrors: setShowLocalErrors,
	}

	const listTags = React.useCallback(() => {
		return [
			{
				label: t("partner_form.operator", { ns: "partner" }),
				id: "operator",
				visibilty: isAdminOCode,
			},
			{
				label: t("partner_form.contactDetails", { ns: "partner" }),
				id: "address",
				visibilty: true,
			},
			{
				label: t("partner_form.legalInfo", { ns: "partner" }),
				id: "structure",
				visibilty: true,
			},
			{
				label: `${t("partner_form.services", { ns: "partner", operator: "MFC" })}`,
				id: "services",
				visibilty: isAdminMFC || updatedPartner?.isMFC,
			},
			{
				label: t("partner_form.billing", { ns: "partner" }),
				id: "billing",
				visibilty:
					(isAdminBicycode || isAdminOCode) &&
					updatedPartner?.isBicycode &&
					isPartnerAlreadyRegistered,
			},
			{
				label: t("partner_form.manager", { ns: "partner" }),
				id: "manager",
				visibilty: true,
			},
			{
				label: t("partner_form.specificFeature", { ns: "partner" }),
				id: "specification",
				visibilty: true,
			},
		]
	}, [t, isAdminMFC, isPartnerAlreadyRegistered, isAdminOCode, updatedPartner])

	// Tous les blocs sont éditables lors de la création d'un partenaire
	// A la modification, les blocs sont éditables dès le moment où l'on clique sur "modifier" et si je ne suis pas connecté en tant qu'admin client
	// A date (janvier 2024), un admin opérateur pourra modifier : le siret, la raison sociale d'un partenaire et cocher/décocher la case "Opérateur"
	// isPartnerAlreadyRegistered === false => création d'un partenaire
	const isBlockEditableByUser = (editForm && !isAdminOperator) || !isPartnerAlreadyRegistered

	const onCancelEdit = () => {
		setEditForm(false)
	}

	const hasFleetSelected = React.useMemo(() => {
		return updatedPartner?.mfc_is_fleet || updatedPartner?.bc_is_fleet
	}, [updatedPartner])

	return (
		<ProtectedLayout className={"py-7 " + (partner?.status !== 0 ? "lg:py-7" : "")}>
			{!isNotEmpty(partner) ? (
				<div className="flex justify-center items-center py-12">
					<div className="c-spinner"></div>
				</div>
			) : (
				<div className="l-container">
					<div
						className={clsx(
							"c-section-title mb-7" + (partner.status === 0 ? "mt-7" : ""),
							{ "px-8": !isMobileOnly, "px-6": isMobileOnly }
						)}
					>
						<div className="c-section-title__content justify-between">
							<div className="flex justify-between w-full">
								<div className="flex items-center justify-start">
									<button
										onClick={() => navigate(-1)}
										className="flex"
										aria-label={t("global.back")}
									>
										<span className="flex py-2">
											<CaretIcon size={26} />
										</span>
									</button>
									<h1 className="c-section-title__title c-h2 text-primary ml-4">
										{isPartnerAlreadyRegistered && (
											<>
												{/*partner.status === 1 && (
													<>{t("title_edit", { ns: "partner" })} </>
												)*/}
												{partner.raison_sociale}
											</>
										)}
										{!isPartnerAlreadyRegistered &&
											t("title_add", { ns: "partner" })}
									</h1>
								</div>

								<div className="flex gap-2">
									{editForm ? (
										<>
											<button
												onClick={onCancelEdit}
												className="c-btn c-btn--red c-btn--s px-7"
											>
												{t("global.cancel")}
											</button>
											<button
												onClick={handleUpdatePartner}
												className="c-btn c-btn--primary c-btn--m px-12"
											>
												{t("global.save")}
											</button>
										</>
									) : (
										<>
											{
												// Seuls les admins ocode, BC et Moustache peuvent se connecter en tant que partenaire
												(isAdminOCode ||
													isAdminBicycode ||
													isAdminMoustache) &&
													isPartnerAlreadyRegistered && (
														<button
															className="c-btn c-btn--primary c-btn--s px-7"
															onClick={connectAsPartner}
														>
															{t("support.connect_as")}
														</button>
													)
											}
											<>
												{!isPartnerAlreadyRegistered ? (
													<button
														type="submit"
														className="c-btn c-btn--primary c-btn--m px-12"
														onClick={handleCreatePartner}
														disabled={!formEvents.ready}
													>
														{t(
															!isPartnerAlreadyRegistered
																? "global.create"
																: "global.update"
														)}
													</button>
												) : (
													<button
														type="submit"
														className="c-btn c-btn--primary c-btn--m px-12"
														onClick={handleEdit}
														disabled={!formEvents.ready}
													>
														{t(
															!isPartnerAlreadyRegistered
																? "global.create"
																: "global.update"
														)}
													</button>
												)}
											</>
										</>
									)}
								</div>
							</div>
						</div>
					</div>

					<div
						className={clsx({
							"px-8": !isMobileOnly,
							"px-6": isMobileOnly,
						})}
					>
						{/* Navigation */}
						{partner.status === 1 && (
							<div className="c-submenu px-7 pb-7">
								<div className="flex items-center justify-start w-full">
									<div className={"c-submenu__item px-5 "}>
										<NavLink
											to={`${privateRoutes.partner.basePath}/${partner.entity_id}`}
											className="flex"
										>
											{t("menu.store")}
										</NavLink>
									</div>
									<div className={"c-submenu__item px-5 "}>
										<NavLink
											to={`${privateRoutes.teamPartner.basePath}/${partner.entity_id}`}
											className="flex"
										>
											{t("menu.manage_team")}
										</NavLink>
									</div>
								</div>
							</div>
						)}
					</div>
					<div className="c-card mx-8 p-8">
						<div className="flex gap-4 flex-wrap p-4 box-border ">
							{listTags().map((element, key) => {
								if (element.visibilty)
									return (
										<button
											key={key}
											onClick={() =>
												// Ajout d'une classe scroll-mt-XX sur le container de chaque bloc pour créer un décélage avec le header fixe
												document
													.getElementById(element.id)
													.scrollIntoView({ behavior: "smooth" })
											}
											type="button"
										>
											{element.label}
										</button>
									)
							})}
						</div>

						{isAdmin && isPartnerAlreadyRegistered && (
							<>
								<ReferenceForm
									{...componentCommonProps}
									refresh={fetchData}
									isEdit={!isPartnerAlreadyRegistered}
								/>
								<hr className="h-1px bg-neutral400" />
							</>
						)}

						{isAdmin && (
							<>
								<OperatorsForm
									{...componentCommonProps}
									isEdit={editForm || !isPartnerAlreadyRegistered}
								/>
								<hr className="h-1px bg-neutral400" />
							</>
						)}

						<AddressForm
							{...componentCommonProps}
							editCallback={editPartner}
							isEdit={isBlockEditableByUser}
							isViewMode={partner?.gender === VELOCISTE_GENDER && isAdminBicycode}
						/>

						<hr className="h-1px bg-neutral400" />

						<StructureForm
							{...componentCommonProps}
							isEdit={editForm || !isPartnerAlreadyRegistered}
							isViewMode={partner?.gender === VELOCISTE_GENDER && isAdminBicycode}
							isPartnerAlreadyRegistered={isPartnerAlreadyRegistered}
						/>

						<hr className="h-1px bg-neutral400" />

						{/*
							Le bloc est visible si je suis un admin MFC ou si le partenaire est un partenaire MFC (case cochée dans le bloc "Structure")
						*/}
						{(isAdminMFC || updatedPartner?.isMFC) && (
							<>
								<ServicesForm
									{...componentCommonProps}
									editCallback={editPartner}
									isEdit={isBlockEditableByUser}
									isMFCSelected={updatedPartner?.isMFC}
								/>
								<hr className="h-1px bg-neutral400" />
							</>
						)}

						<ManagerForm
							{...componentCommonProps}
							editCallback={editPartner}
							refresh={fetchData}
							isEdit={isBlockEditableByUser}
							isViewMode={partner?.gender === VELOCISTE_GENDER && isAdminBicycode}
						/>

						{/*
					We keep 
					<WalletForm
						{...componentCommonProps}
						editCallback={editPartner}
						isEdit={editForm && isPartnerAlreadyRegistered}
						isViewMode={partner?.gender === VELOCISTE_GENDER && isAdminBicycode}
					/>*/}

						<hr className="h-1px bg-neutral400" />

						<BikeCollectionImportForm
							hasFleetSelected={hasFleetSelected}
							{...componentCommonProps}
							editCallback={editPartner}
							isEdit={editForm || !isPartnerAlreadyRegistered}
							isViewMode={partner?.gender === VELOCISTE_GENDER && isAdminBicycode}
							isBCSelected={partner?.isBicycode && isAdminOCode}
						/>
						{(updatedPartner?.isBicycode || updatedPartner?.isMFC) && (
							<>
								<hr className="h-1px bg-neutral400" />

								<BikeFleetAccountIdForm
									isBCSelected={updatedPartner?.isBicycode}
									isMFCSelected={updatedPartner?.isMFC}
									{...componentCommonProps}
									editCallback={editPartner}
									refresh={fetchData}
									isEdit={editForm || !isPartnerAlreadyRegistered}
								/>
							</>
						)}

						{(isAdminBicycode || isAdminOCode) &&
							partner.isBicycode &&
							isPartnerAlreadyRegistered && (
								<>
									<hr className="h-1px bg-neutral400" />
									<BilingForm
										{...componentCommonProps}
										editCallback={editPartner}
										isEdit={true}
										isViewMode={true}
										isAdminEditing={isAdminBicycode || isAdminOCode}
									/>
									{!isProductionEnvironment && (
										<>
											<hr className="h-1px bg-neutral400" />
											<MandateForm
												{...componentCommonProps}
												isEdit={true}
												isViewMode={true}
												isAdminViewing={isAdminOCode || isAdminBicycode}
											/>
										</>
									)}
								</>
							)}
					</div>
				</div>
			)}
		</ProtectedLayout>
	)
}
